import { Box, IconButton, Tooltip, Typography, keyframes } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import React from "react";
import {
  useFetchSubscriptionListener,
  useStartSubscription,
  useStopSubscription,
  useSubscription,
} from "../../../hook/page/block/subscription";
import { SubscriptionBlock } from "../../../types/notebook/note/block/subscription";
import { CopyClipboard } from "../../copyClipboard";
import { NoteBlockContainer } from "../../note";
import { CasLoader } from "../../brand/CasLoader";

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(0.65); }
`;

export const SubscriptionBlockComponent: React.FC<{ block: SubscriptionBlock }> = ({
  block,
}) => {
  const { "sub-id": subId } = block;
  useFetchSubscriptionListener(subId);
  const subscription = useSubscription(subId);

  const [controlling, setControlling] = React.useState(false);
  const startSub = useStartSubscription(block);
  const stopSub = useStopSubscription(block);

  const isStarted = subscription?.status === "started";
  const isStopped = subscription?.status === "stopped";

  const handleStart = async () => {
    setControlling(true);
    await startSub();
    setControlling(false);
  };
  const handleStop = async () => {
    setControlling(true);
    await stopSub();
    setControlling(false);
  };

  const shortId = `${subId.slice(0, 8)}…`;

  return (
    <NoteBlockContainer name="Subscription">
      {/* Status + controls row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 22 }}>
        {/* Live status dot */}
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            flexShrink: 0,
            bgcolor: isStarted ? "success.main" : subscription ? "text.disabled" : "action.disabled",
            animation: isStarted ? `${pulse} 2s ease-in-out infinite` : "none",
          }}
        />

        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontSize: "0.75rem", lineHeight: 1 }}
        >
          {!subscription ? "loading" : subscription.status}
        </Typography>

        {subscription?.name && (
          <Typography
            variant="caption"
            sx={{ color: "text.disabled", fontSize: "0.72rem", lineHeight: 1 }}
          >
            · {subscription.name}
          </Typography>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Spinner while controlling */}
        {controlling && (
          <CasLoader size={14} label="Updating subscription" />
        )}

        {/* Play / Stop icon button */}
        {subscription && !controlling && (
          <>
            {isStopped && (
              <Tooltip title="Start subscription" placement="top">
                <IconButton
                  size="small"
                  onClick={handleStart}
                  sx={{
                    p: "2px",
                    color: "success.main",
                    transition: "background 0.12s, color 0.12s",
                    "&:hover": { bgcolor: "success.main", color: "success.contrastText" },
                  }}
                >
                  <PlayArrowIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
            {isStarted && (
              <Tooltip title="Stop subscription" placement="top">
                <IconButton
                  size="small"
                  onClick={handleStop}
                  sx={{
                    p: "2px",
                    color: "error.main",
                    transition: "background 0.12s, color 0.12s",
                    "&:hover": { bgcolor: "error.main", color: "error.contrastText" },
                  }}
                >
                  <StopIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </Box>

      {/* ID row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography
          variant="caption"
          sx={{ color: "text.disabled", fontSize: "0.68rem", userSelect: "none", mr: 0.25 }}
        >
          ID
        </Typography>
        <Tooltip title={subId} placement="bottom-start">
          <Typography
            variant="caption"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.72rem",
              color: "text.secondary",
              cursor: "default",
              letterSpacing: "0.01em",
            }}
          >
            {shortId}
          </Typography>
        </Tooltip>
        <CopyClipboard value={subId} />
      </Box>

    </NoteBlockContainer>
  );
};
